import { useState } from "react";
import { toast } from "sonner";
import { Route, Sparkles, Loader2, FileText, TrendingUp, User, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { generateRoadmap } from "@/lib/api";
import SafeMarkdown from "@/components/SafeMarkdown";

const PRIORITY_STYLE = {
  high:   "bg-red-100 text-red-800 ring-red-200",
  medium: "bg-amber-100 text-amber-800 ring-amber-200",
  low:    "bg-blue-100 text-blue-800 ring-blue-200",
};

const QUARTER_ACCENT = {
  Q1: { bg: "bg-blue-50", border: "border-blue-300", dot: "bg-blue-600", text: "text-blue-900" },
  Q2: { bg: "bg-emerald-50", border: "border-emerald-300", dot: "bg-emerald-600", text: "text-emerald-900" },
  Q3: { bg: "bg-amber-50", border: "border-amber-300", dot: "bg-amber-600", text: "text-amber-900" },
  Q4: { bg: "bg-purple-50", border: "border-purple-300", dot: "bg-purple-600", text: "text-purple-900" },
};

export default function RoadmapPanel({ reclassified, ratios, sector, correctives, roadmap, onChange, includeInPdf, onIncludeInPdfChange }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await generateRoadmap(reclassified, ratios, correctives, sector, lang);
      if (res.error) {
        toast.warning(lang === "it"
          ? "Roadmap AI non disponibile al momento"
          : "Roadmap AI unavailable at the moment");
      } else {
        onChange?.(res);
        toast.success(t("roadmap_generated"));
      }
    } catch (e) {
      toast.error(t("advisor_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="roadmap-panel">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-purple-50 text-purple-700">
            <Route className="h-4 w-4" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">{t("roadmap_title")}</div>
            <div className="text-xs text-slate-500">{t("roadmap_subtitle")}</div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={generate}
          disabled={loading}
          data-testid="roadmap-generate-btn"
          className="rounded-full bg-purple-700 text-white hover:bg-purple-800"
        >
          {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />}
          {roadmap ? t("roadmap_regenerate") : t("roadmap_generate")}
        </Button>
      </div>

      {loading && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("roadmap_generating")}
        </div>
      )}

      {!roadmap && !loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="roadmap-empty">
          <Route className="mx-auto mb-3 h-8 w-8 text-slate-400" strokeWidth={1.5} />
          <div className="mb-2 text-sm font-medium text-slate-800">{t("roadmap_empty_title")}</div>
          <div className="mx-auto max-w-md text-xs text-slate-500">{t("roadmap_empty_desc")}</div>
        </div>
      )}

      {roadmap?.quarters?.length > 0 && (
        <>
          <div className="relative space-y-6">
            {/* Vertical timeline line */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gradient-to-b from-blue-400 via-amber-400 to-purple-400 opacity-40" />
            {roadmap.quarters.map((q) => {
              const accent = QUARTER_ACCENT[q.label] || QUARTER_ACCENT.Q1;
              return (
                <div key={q.label} className="relative pl-12" data-testid={`roadmap-quarter-${q.label}`}>
                  <div className={`absolute left-2.5 top-3 grid h-4 w-4 place-items-center rounded-full ring-4 ring-white ${accent.dot}`}>
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <div className={`rounded-xl border ${accent.border} ${accent.bg} p-5`}>
                    <div className="mb-3 flex flex-wrap items-baseline gap-3">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${accent.text} bg-white/70`}>
                        {q.label} {q.period_it || q.period_en || q.period ? `· ${q.period_it || q.period_en || q.period}` : ""}
                      </span>
                      <h3 className={`font-heading text-base font-semibold ${accent.text}`}>{q.focus}</h3>
                    </div>

                    {q.actions?.length > 0 && (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {q.actions.map((a, ai) => (
                          <div key={a.title || `${q.label}-action-${ai}`} className="rounded-lg border border-white/70 bg-white/80 p-3 shadow-sm">
                            <div className="mb-1.5 flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{a.title}</div>
                              <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.medium}`}>
                                {a.priority || "medium"}
                              </span>
                            </div>
                            {a.description && (
                              <div className="mb-2 text-xs leading-relaxed text-slate-600">{a.description}</div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 text-[11px]">
                              {a.owner && (
                                <span className="inline-flex items-center gap-1 text-slate-600">
                                  <User className="h-3 w-3" strokeWidth={2} />
                                  <b>{a.owner}</b>
                                </span>
                              )}
                              {a.kpi && (
                                <span className="inline-flex items-center gap-1 text-slate-600">
                                  <Target className="h-3 w-3" strokeWidth={2} />
                                  {a.kpi}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {q.expected_impact && (
                      <div className="mt-4 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
                        <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                        <div><b>{t("roadmap_impact")}:</b> {q.expected_impact}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {roadmap.summary && (
            <div className="mt-6 rounded-xl border border-purple-200 bg-purple-50/40 p-5" data-testid="roadmap-summary">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800">
                <Sparkles className="h-3.5 w-3.5" /> {lang === "it" ? "Sintesi del piano" : "Plan summary"}
              </div>
              <div className="space-y-1"><SafeMarkdown text={roadmap.summary} testId="roadmap-summary-md" /></div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between rounded-lg border border-purple-100 bg-purple-50/50 px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white text-purple-700 ring-1 ring-purple-200">
                <FileText className="h-4 w-4" strokeWidth={2} />
              </div>
              <div>
                <Label htmlFor="roadmap-include-pdf-switch" className="cursor-pointer text-sm font-medium text-slate-900">
                  {t("roadmap_include_pdf")}
                </Label>
                <div className="text-xs text-slate-500">
                  {lang === "it" ? "La roadmap sarà inclusa nel PDF esportato" : "The roadmap will be appended to the exported PDF"}
                </div>
              </div>
            </div>
            <Switch
              id="roadmap-include-pdf-switch"
              data-testid="roadmap-include-pdf-switch"
              checked={!!includeInPdf}
              onCheckedChange={(v) => onIncludeInPdfChange?.(v)}
            />
          </div>
        </>
      )}
    </div>
  );
}
